import React from 'react';
import { Typography, Card, Space, Divider, Alert, List, Tag } from 'antd';
import { Scale, AlertTriangle, Shield, UserX, CheckCircle, XCircle } from 'lucide-react';

const { Title, Paragraph, Text } = Typography;

const Terms = () => {
    const effectiveDate = "October 30, 2024";

    const acceptableUse = [
        "Professional meeting assistance and guidance",
        "Personal meeting enhancement and improvement",
        "Team collaboration and communication",
        "Meeting preparation and follow-up",
        "Learning and self-improvement purposes"
    ];

    const prohibitedUse = [
        {
            title: "Content Manipulation",
            description: "Using the service to generate misleading or false meeting transcripts or recommendations"
        },
        {
            title: "Unauthorized Recording",
            description: "Recording meetings without explicit consent from all participants"
        },
        {
            title: "Harmful Content",
            description: "Using the service to generate harmful, threatening, or discriminatory content"
        },
        {
            title: "Commercial Exploitation",
            description: "Reselling or redistributing ELI's recommendations without authorization"
        },
        {
            title: "System Interference",
            description: "Attempting to interfere with, compromise, or disable service features"
        }
    ];

    const userResponsibilities = [
        {
            title: "Account Security",
            items: [
                "Maintain confidentiality of login credentials",
                "Use strong passwords and enable 2FA when available",
                "Report unauthorized access immediately",
                "Logout from shared devices"
            ]
        },
        {
            title: "Content Responsibility",
            items: [
                "Ensure meeting content complies with applicable laws",
                "Obtain necessary permissions for meeting recordings",
                "Respect intellectual property rights",
                "Maintain professional conduct during meetings"
            ]
        }
    ];

    const serviceLimitations = [
        "ELI provides suggestions based on AI analysis but final decisions rest with users",
        "Service availability may vary based on technical and operational factors",
        "Accuracy of transcriptions depends on audio quality and speaking clarity",
        "Real-time recommendations may have slight delays based on processing requirements",
        "Some features may be limited in certain geographical locations"
    ];

    const refundPolicy = {
        eligiblePeriod: [
            "Within 14 days of subscription start date",
            "Within 30 days for annual subscriptions"
        ],
        conditions: [
            {
                title: "Full Refund",
                description: "Available if service has not been used"
            },
            {
                title: "Partial Refund",
                description: "Prorated based on usage period"
            },
            {
                title: "Technical Issues",
                description: "Full refund if service is unavailable due to technical problems"
            }
        ],
        exclusions: [
            "Service abuse or Terms of Service violations",
            "Requests after eligible refund period",
            "Used API credits or special promotional offers"
        ],
        process: [
            "Submit refund request via official email",
            "Include purchase date, account information, and reason for refund",
            "Processing time: 5-7 business days"
        ]
    };

    return (
        <div className="max-w-5xl mx-auto">
            <Card className="shadow-lg border-0">
                <Space direction="vertical" size="large" className="w-full">
                    <div className="text-center pb-8">
                        <Title level={2}>Terms of Service</Title>
                        <Text type="secondary">Effective Date: {effectiveDate}</Text>
                    </div>

                    <Alert
                        message="Important Notice"
                        description="These Terms of Service govern your use of ELI, our AI-powered meeting assistant. By using our service, you agree to these terms in their entirety."
                        type="warning"
                        showIcon
                        className="mb-8"
                    />

                    <section>
                        <Title level={3}>1. Service Description</Title>
                        <Paragraph>
                            ELI is an AI-powered meeting assistant that provides real-time guidance, suggestions, and recommendations during meetings based on transcript analysis. Our service helps users make better decisions and take appropriate actions during meetings through simple interactions with our AI system.
                        </Paragraph>
                    </section>

                    <Divider />

                    <section>
                        <Title level={3}>2. Acceptable Use</Title>
                        <Card className="bg-gray-50 border-0">
                            <List
                                header={<Text strong>The service may be used for:</Text>}
                                dataSource={acceptableUse}
                                renderItem={(item) => (
                                    <List.Item>
                                        <CheckCircle size={16} className="text-green-600 mr-2" />
                                        <Text>{item}</Text>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </section>

                    <Divider />

                    <section>
                        <Title level={3}>3. Prohibited Use</Title>
                        <div className="grid grid-cols-1 gap-4">
                            {prohibitedUse.map((item, index) => (
                                <Card key={index} className="border-0 shadow-sm">
                                    <Space>
                                        <XCircle size={24} className="text-red-600" />
                                        <div>
                                            <Text strong>{item.title}</Text>
                                            <br />
                                            <Text type="secondary">{item.description}</Text>
                                        </div>
                                    </Space>
                                </Card>
                            ))}
                        </div>
                    </section>

                    <Divider />

                    <section>
                        <Title level={3}>4. User Responsibilities</Title>
                        {userResponsibilities.map((category, index) => (
                            <Card key={index} className="mb-4 bg-gray-50 border-0">
                                <Title level={4}>{category.title}</Title>
                                <List
                                    dataSource={category.items}
                                    renderItem={(item) => (
                                        <List.Item>
                                            <Text>{item}</Text>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        ))}
                    </section>

                    <Divider />

                    <section>
                        <Title level={3}>5. Service Limitations</Title>
                        <Card className="bg-gray-50 border-0">
                            <List
                                dataSource={serviceLimitations}
                                renderItem={(item) => (
                                    <List.Item>
                                        <AlertTriangle size={16} className="text-yellow-600 mr-2" />
                                        <Text>{item}</Text>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </section>

                    <Divider />

                    <section>
                        <Title level={3}>6. Termination</Title>
                        <Card className="bg-red-50 border-0">
                            <Paragraph>
                                We reserve the right to suspend or terminate your access to ELI if you:
                            </Paragraph>
                            <List
                                dataSource={[
                                    "Violate these Terms of Service",
                                    "Engage in fraudulent or illegal activities",
                                    "Abuse or misuse the service",
                                    "Fail to pay applicable fees",
                                    "Create risk or possible legal exposure for us"
                                ]}
                                renderItem={(item) => (
                                    <List.Item>
                                        <Text>{item}</Text>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </section>

                    <Divider />

                    <section>
                        <Title level={3}>7. Refund Policy</Title>
                        <Space direction="vertical" className="w-full">
                            <Card className="bg-blue-50 border-0">
                                <Title level={4}>Eligible Refund Period</Title>
                                <List
                                    dataSource={refundPolicy.eligiblePeriod}
                                    renderItem={(item) => (
                                        <List.Item>
                                            <CheckCircle size={16} className="text-green-600 mr-2" />
                                            <Text>{item}</Text>
                                        </List.Item>
                                    )}
                                />
                            </Card>

                            <Card className="bg-gray-50 border-0 mt-4">
                                <Title level={4}>Refund Conditions</Title>
                                <div className="grid grid-cols-1 gap-4">
                                    {refundPolicy.conditions.map((item, index) => (
                                        <div key={index} className="flex items-start space-x-2">
                                            <Scale size={16} className="text-blue-600 mt-1" />
                                            <div>
                                                <Text strong>{item.title}</Text>
                                                <br />
                                                <Text type="secondary">{item.description}</Text>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card>

                            <Card className="bg-red-50 border-0 mt-4">
                                <Title level={4}>Refund Exclusions</Title>
                                <List
                                    dataSource={refundPolicy.exclusions}
                                    renderItem={(item) => (
                                        <List.Item>
                                            <XCircle size={16} className="text-red-600 mr-2" />
                                            <Text>{item}</Text>
                                        </List.Item>
                                    )}
                                />
                            </Card>

                            <Card className="bg-gray-50 border-0 mt-4">
                                <Title level={4}>Refund Process</Title>
                                <List
                                    dataSource={refundPolicy.process}
                                    renderItem={(item) => (
                                        <List.Item>
                                            <Text>{item}</Text>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                        </Space>
                    </section>

                    <Divider />

                    <section>
                        <Title level={3}>8. Contact Information</Title>
                        <Card className="bg-blue-50 border-0">
                            <Space direction="vertical">
                                <Text>EliCorp, Inc.</Text>
                                <Text>Email: elimeetagent@gmail.com</Text>
                                <Text>Address: 131 Continental Dr
                                    Newark, DE 19713
                                    United States</Text>
                                <Text>Tel: +82 (010) 5919-7473</Text>
                            </Space>
                        </Card>
                    </section>

                    <Divider />

                    <footer className="text-center">
                        <Text type="secondary">
                            © {new Date().getFullYear()} EliCorp Labs, Inc. All rights reserved.
                            <br />
                            These Terms of Service were last updated on {effectiveDate}
                        </Text>
                    </footer>
                </Space>
            </Card>
        </div>
    );
};

export default Terms;